import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../../config";

const API_URL = `${API_BASE_URL}/reportIssue`;

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const ReportIssueModal = ({ diagnostics, onClose }) => {
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [notesTouched, setNotesTouched] = useState(false);
  const [success, setSuccess] = useState(false);

  const trimmedEmail = email.trim();
  const emailInvalid = trimmedEmail.length > 0 && !isValidEmail(trimmedEmail);
  const emailError =
    emailTouched && emailInvalid
      ? "Please enter a valid email address or leave this field empty."
      : "";

  const notesEmpty = notes.trim().length === 0;
  const notesError =
    notesTouched && notesEmpty
      ? "Please describe what you were doing when this happened."
      : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setEmailTouched(true);
    setNotesTouched(true);
    if (emailInvalid) {
      setError("Please enter a valid email address or leave the email field empty.");
      return;
    }
    if (notesEmpty) {
      setError("Please describe what you were doing when this happened.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await axios.post(API_URL, {
        email: trimmedEmail || undefined,
        notes: notes.trim(),
        ...diagnostics,
      });
      setSuccess(true);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Could not send your report. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="il-report-issue-overlay" onClick={onClose} role="presentation">
      <div
        className="il-report-issue-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="il-report-issue-title"
      >
        <h2 id="il-report-issue-title" className="il-report-issue-title">
          Report an issue
        </h2>
        <p className="il-report-issue-lead">
          Describe what happened. Session diagnostics are included automatically.
        </p>

        {success ? (
          <>
            <p className="il-report-issue-success">
              Thank you — your report was sent to the support team.
            </p>
            <button
              type="button"
              className="il-report-issue-btn il-report-issue-btn--primary"
              onClick={onClose}
            >
              Close
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <label className="il-report-issue-label" htmlFor="il-report-email">
              Your email <span className="il-report-issue-optional">(optional)</span>
            </label>
            <input
              id="il-report-email"
              type="text"
              inputMode="email"
              autoComplete="email"
              className={`il-report-issue-input${emailError ? " il-report-issue-input--invalid" : ""}`}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              onBlur={() => setEmailTouched(true)}
              placeholder="you@example.com"
              disabled={submitting}
              aria-invalid={emailInvalid}
              aria-describedby={emailError ? "il-report-email-error" : undefined}
            />
            {emailError && (
              <p id="il-report-email-error" className="il-report-issue-field-error" role="alert">
                {emailError}
              </p>
            )}

            <label className="il-report-issue-label" htmlFor="il-report-notes">
              What were you doing when this happened?{" "}
              <span className="il-report-issue-required">*</span>
            </label>
            <textarea
              id="il-report-notes"
              className={`il-report-issue-textarea${notesError ? " il-report-issue-input--invalid" : ""}`}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                if (error) setError("");
              }}
              onBlur={() => setNotesTouched(true)}
              placeholder="e.g. I was exporting a video when the stream disconnected…"
              rows={4}
              disabled={submitting}
              aria-invalid={notesTouched && notesEmpty}
              aria-describedby={notesError ? "il-report-notes-error" : undefined}
            />
            {notesError && (
              <p id="il-report-notes-error" className="il-report-issue-field-error" role="alert">
                {notesError}
              </p>
            )}

            {error && !emailError && !notesError && (
              <p className="il-report-issue-error" role="alert">
                {error}
              </p>
            )}

            <div className="il-report-issue-actions">
              <button
                type="button"
                className="il-report-issue-btn il-report-issue-btn--secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="il-report-issue-btn il-report-issue-btn--primary"
                disabled={submitting || emailInvalid}
              >
                {submitting ? "Sending…" : "Send report"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportIssueModal;
